/**
 * bossDamage.ts
 * Apply damage to the Void Mothership boss, honouring the directional
 * shield gate and the hit-flash window.
 *
 * Extracted out of {@link ../entities/VoidMothership} to keep the entity
 * under the 300-line budget — mirrors the existing engine-side split
 * (`bossRam`, `bossMissileBarrage`, `bossDroneSpawner`, ...).
 *
 * @module bossDamage
 */

import { isShieldedHitBlocked } from './shieldGate';
import { VOID_MOTHERSHIP_HIT_FLASH_DURATION_S } from '../../constants/bossConstants';
import type { VoidMothership } from '../entities/VoidMothership';
import Logger from '../utils/logger';

/**
 * Apply `amount` damage to the boss. Marks the boss dead when HP
 * reaches 0. Shielded hits are blocked unless the hit point falls in
 * an angular gap between shield nodes.
 *
 * @param boss   - Boss entity to mutate.
 * @param amount - Damage in HP units. Non-positive values are ignored.
 * @param hitX   - Optional world-space X of the projectile impact.
 *                 When omitted, shielded hits are always blocked.
 * @param hitY   - Optional world-space Y. Must accompany `hitX`.
 * @returns `true` if the boss was killed by this hit.
 */
export function applyBossDamage(
  boss: VoidMothership,
  amount: number,
  hitX?: number,
  hitY?: number,
): boolean {
  if (boss.dead || amount <= 0) return false;
  // DECISION: Shield gates HP only, not projectile consumption. The
  // collision resolver still sets `p.dead = true` on its side so a
  // shielded hit produces an audible/visible impact, but no HP is
  // removed unless the hit threads a node gap (or EXPOSED).
  if (boss.isShielded) {
    // Missing hit coords default to fully blocked — keeps legacy
    // callers that don't yet pass geometry working unchanged.
    if (hitX === undefined || hitY === undefined) return false;
    const blocked = isShieldedHitBlocked({
      bossCenterX: boss.x + boss.width / 2,
      bossCenterY: boss.y + boss.height / 2,
      shieldAngle: boss.shieldAngle,
      hitX,
      hitY,
    });
    if (blocked) return false;
  }
  boss.hp -= amount;
  // Flash is gated on a real HP change, not on the call itself — a
  // blocked hit returns above without flashing, matching the player's
  // read of "no progress yet".
  boss.hitFlashTimer = VOID_MOTHERSHIP_HIT_FLASH_DURATION_S;
  if (boss.hp <= 0) {
    boss.hp = 0;
    boss.dead = true;
    boss.active = false;
    boss.state = 'DEFEATED';
    Logger.debug('VoidMothership', 'HP depleted → DEFEATED');
    return true;
  }
  return false;
}
